/**
 * @description Auth boot chrome helpers — HTML sibling overlay + one-shot dismiss
 *
 * Key features:
 * - Path helpers aligned with public/index.html inline script
 * - dismissBootSplash() hides #boot-splash and drops html.auth-boot
 * - showBootSplash() restores chrome for a later SPA wait (forgot-password → sign-in)
 * - BootSplashDismisser / BootSplashKeeper run those in useLayoutEffect
 *
 * @dependencies
 * - react: useLayoutEffect
 *
 * @notes
 * - The spinner lives in public/index.html as a sibling of #root (fixed overlay).
 *   React must not paint a second splash — remounting resets CSS animation.
 * - Dismiss only after branded form, real error, or signed-in shell is committed.
 * - Do not dismiss from App on `/`, `/sign-in`, `/sign-up`.
 * - Do not dismiss from App on signed-out protected URLs — ProtectedRoute
 *   Navigate-to-sign-in would flash the light SignedOut shell first.
 *   Public pages (privacy, terms, onboarding, short links) must still dismiss.
 * - Every unsigned Navigate to sign-in must use UnsignedSignInRedirect so
 *   an already-dismissed overlay is restored (ProtectedRoute and `/`).
 * - After a dismiss, SPA nav back to SignIn (isCheckingAuth) must restore chrome
 *   or #root stays empty. Unhiding restarts the CSS animation — that is a new wait.
 */

import { useLayoutEffect } from 'react';
import { Navigate } from 'react-router-dom';

export const AUTH_BOOT_MESSAGE = 'Preparing sign in...';

/** Keep in lockstep with frontend/public/index.html inline path checks. */
export const AUTH_BOOT_PREFIXES = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/accept-company-invite',
  '/auth/callback',
];

/**
 * Auth routes whose page component paints immediately (form / callback shell).
 * App may dismiss chrome on these paths. /sign-in and /sign-up must not —
 * they return null until the Wristband request token (or branding) is ready.
 */
export const IMMEDIATE_AUTH_FORM_PREFIXES = [
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/accept-company-invite',
  '/auth/callback',
];

/**
 * Trees wrapped in ProtectedRoute. Signed-out visits must keep chrome until
 * Navigate-to-sign-in commits — public pages are not in this list.
 * Keep in lockstep with frontend/src/App.js ProtectedRoute prefixes.
 */
export const PROTECTED_APP_PREFIXES = [
  '/dashboard',
  '/merchant',
  '/so',
];

let bootSplashDismissed = false;

/**
 * @param {string} path
 * @param {string} prefix
 * @returns {boolean}
 */
function pathMatchesPrefix(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Paths that keep HTML boot chrome until the form, error, or signed-in shell commits.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isAuthBootPath(pathname) {
  const path = String(pathname || '');
  if (path === '/') return true;
  return AUTH_BOOT_PREFIXES.some((prefix) => pathMatchesPrefix(path, prefix));
}

/**
 * Paths that redirect to sign-in when unsigned. App keeps chrome here until
 * isSignedIn so ProtectedRoute does not flash the light SignedOut shell.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isProtectedAppPath(pathname) {
  const path = String(pathname || '');
  return PROTECTED_APP_PREFIXES.some((prefix) => pathMatchesPrefix(path, prefix));
}

/**
 * Copy for the HTML boot label. Signed-in / dashboard hydrations stay spinner-only.
 *
 * @param {string} pathname
 * @param {{ hasSession?: boolean }} [options]
 * @returns {string}
 */
export function authBootMessage(pathname, { hasSession = false } = {}) {
  if (hasSession) return '';
  return isAuthBootPath(pathname) ? AUTH_BOOT_MESSAGE : '';
}

/**
 * Whether App may dismiss HTML chrome on this path.
 * False for `/`, `/sign-in`, `/sign-up` so chrome stays while those trees return null.
 * Protected trees dismiss only when signed in — otherwise ProtectedRoute
 * redirects and a one-frame light SignedOut shell would flash.
 * Other non-boot paths (privacy, terms, onboarding, short links) dismiss
 * so a signed-out visitor is not trapped under the overlay.
 *
 * @param {string} pathname
 * @param {{ isSignedIn?: boolean }} [options]
 * @returns {boolean}
 */
export function shouldDismissBootSplashFromApp(pathname, { isSignedIn = false } = {}) {
  if (isAuthBootPath(pathname)) {
    return IMMEDIATE_AUTH_FORM_PREFIXES.some((prefix) => pathMatchesPrefix(pathname, prefix));
  }
  if (isProtectedAppPath(pathname)) return !!isSignedIn;
  return true;
}

/**
 * Hide #boot-splash and drop html.auth-boot. Idempotent until showBootSplash().
 */
export function dismissBootSplash() {
  if (bootSplashDismissed) return;
  bootSplashDismissed = true;
  if (typeof document === 'undefined') return;
  const el = document.getElementById('boot-splash');
  if (el) el.hidden = true;
  document.documentElement.classList.remove('auth-boot');
}

/**
 * Restore HTML chrome for a later boot wait (SPA re-entry to /sign-in or /sign-up).
 * No-op if already visible. Unhiding after dismiss starts a new spin — a new wait.
 */
export function showBootSplash() {
  bootSplashDismissed = false;
  if (typeof document === 'undefined') return;
  const el = document.getElementById('boot-splash');
  if (el && el.hidden) el.hidden = false;
  document.documentElement.classList.add('auth-boot');
}

/**
 * Jest-only: allow a later test to dismiss chrome again.
 */
export function resetBootSplashDismissedForTests() {
  bootSplashDismissed = false;
}

/**
 * @param {object} props
 * @param {boolean} [props.ready]
 */
export function BootSplashDismisser({ ready = false }) {
  useLayoutEffect(() => {
    if (ready) dismissBootSplash();
  }, [ready]);
  return null;
}

/**
 * Keep or restore HTML chrome while a route returns no ready UI.
 *
 * @param {object} [props]
 * @param {boolean} [props.waiting]
 */
export function BootSplashKeeper({ waiting = true }) {
  useLayoutEffect(() => {
    if (waiting) showBootSplash();
  }, [waiting]);
  return null;
}

/**
 * Restore chrome and bounce to sign-in in the same commit.
 * Use this for every unsigned Navigate to sign-in — ready=false does not
 * unhide an already-dismissed overlay (public page → `/` or a protected URL).
 *
 * @param {object} [props]
 * @param {string} [props.to]
 * @param {object} [props.state]
 */
export function UnsignedSignInRedirect({ to = '/sign-in', state }) {
  return (
    <>
      <BootSplashKeeper />
      <Navigate to={to} state={state} replace />
    </>
  );
}

export default BootSplashDismisser;
